// The AuthClient seam (Testing §3 "Auth"): Firebase in the apps, FakeAuthClient in FR tests and stories. Nothing
// outside this folder knows about Firebase (Frontend §1.2).

export interface AuthUser {
  uid: string
  email: string
}

/** A Firebase-style failure: `code` is the SDK's code, for example `auth/invalid-credential`. */
export class AuthError extends Error {
  readonly code: string
  constructor(code: string, message = code) {
    super(message)
    this.name = 'AuthError'
    this.code = code
  }
}

export const isAuthError = (e: unknown): e is AuthError =>
  e instanceof AuthError || (typeof e === 'object' && e !== null && typeof (e as { code?: unknown }).code === 'string')

/** The Firebase web config of a staff app (`VITE_FIREBASE_*`, Frontend §1.6). */
export interface FirebaseWebConfig {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
  /** `host:port` of the Auth emulator: local runs and tests only (refused when `VITE_ENV=prod`). */
  emulatorHost?: string | undefined
}

/** What the apps need from Firebase Auth (Frontend §5.1). Roles never come from here (D-06). */
export interface AuthClient {
  /** Fires once with the current user when known, then on every sign-in, sign-out and token change. */
  onChange(listener: (user: AuthUser | null) => void): () => void
  signIn(email: string, password: string): Promise<void>
  signOut(): Promise<void>
  /** A-04: `updatePassword`, then `getIdToken(true)`. */
  changePassword(newPassword: string): Promise<void>
  getIdToken(forceRefresh?: boolean): Promise<string | null>
  /** A-02: Firebase's reset email, returning to `continueUrl` (Frontend §5.1). May reject; the page never reveals. */
  sendPasswordReset(email: string, continueUrl: string): Promise<void>
  /** A-03: checks the link's `oobCode` and returns the account's email (`auth/expired-action-code`, …). */
  verifyPasswordResetCode(oobCode: string): Promise<string>
  /** A-03: sets the new password with the link's `oobCode`. */
  confirmPasswordReset(oobCode: string, newPassword: string): Promise<void>
}
